/**
 * Commit Feed — every push, logged against the active shift.
 *
 * Fully redesigned render over the existing real-data pipeline (backend
 * commit stream + GitHub recent-changes merge, bridge events, member
 * resolution). Self-contained styling (dashboard-pages.css).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Activity,
  Award,
  CheckCircle2,
  Clock3,
  GitBranch,
  GitCommitHorizontal,
  GitPullRequest,
} from 'lucide-react';
import { useTeamDashboard } from '../TeamDashboardContext';
import { emitBridgeEvent } from '@/services/crossDashboardBridge';
import './dashboard-pages.css';

const T = {
  accent: '#6e6bff',
  accent2: '#a855f7',
  cyan: '#22d3ee',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb4d6d',
};

const FONT_ID = 'tdp-fonts';
function ensureFonts() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

interface FeedCommit {
  initials: string;
  name: string;
  rawAuthorName?: string;
  knownMember?: boolean;
  hash: string;
  msg: string;
  branch: string;
  time: string;
  color: string;
  ts: string;
}

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('scrudix_token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function githubApiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: 'GET',
    credentials: 'include',
    headers: { ...getAuthHeaders() },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as Record<string, unknown>));
    const msg = typeof (body as { error?: unknown }).error === 'string' ? (body as { error: string }).error : '';
    throw new Error(msg || `GitHub API request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

function relativeTime(input: string): string {
  if (!input) return '-';
  const then = new Date(input).getTime();
  if (!Number.isFinite(then)) return '-';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function parseRepoFullName(input: string): string | null {
  const trimmed = String(input || '').trim();
  if (!trimmed) return null;
  const httpsMatch = trimmed.match(/github\.com\/([^/]+)\/([^/#?]+)/i);
  if (httpsMatch) return `${httpsMatch[1]}/${httpsMatch[2]}`;
  const cleaned = trimmed.replace(/^https?:\/\//i, '').replace(/^github\.com\//i, '').replace(/^\//, '');
  const parts = cleaned.split('/').filter(Boolean);
  if (parts.length < 2) return null;
  return `${parts[0]}/${parts[1].replace(/\.git$/i, '')}`;
}

function normalizeRepo(input: string): string {
  const raw = String(input || '').trim().toLowerCase();
  if (!raw) return '';
  const match = raw.match(/github\.com\/([^/]+)\/([^/#?]+)/i);
  if (match) return `${match[1]}/${match[2].replace(/\.git$/, '')}`;
  const cleaned = raw.replace(/^https?:\/\//, '').replace(/^github\.com\//, '').replace(/^\//, '');
  const parts = cleaned.split('/').filter(Boolean);
  if (parts.length >= 2) return `${parts[0]}/${parts[1].replace(/\.git$/, '')}`;
  return cleaned.replace(/\.git$/, '');
}

const isoMinutesAgo = (minutes: number): string => new Date(Date.now() - minutes * 60_000).toISOString();

const ALL_COMMITS: FeedCommit[] = [
  { initials: 'PK', name: 'Priya Kapoor', hash: 'a3f92c1', msg: 'feat: implement auth middleware', branch: 'feature/auth', time: '2m ago', color: '#3ecf8e', ts: isoMinutesAgo(2) },
  { initials: 'AK', name: 'Arjun Kumar', hash: 'b87e4d2', msg: 'fix: resolve JWT refresh edge case', branch: 'fix/jwt', time: '18m ago', color: '#4f39e3', ts: isoMinutesAgo(18) },
  { initials: 'SR', name: 'Sneha Rao', hash: 'c12ab34', msg: 'refactor: clean up DB schema migrations', branch: 'main', time: '1h ago', color: '#f5a623', ts: isoMinutesAgo(60) },
  { initials: 'AK', name: 'Arjun Kumar', hash: 'd9fe712', msg: 'chore: update dependency versions', branch: 'main', time: '2h ago', color: '#4f39e3', ts: isoMinutesAgo(120) },
  { initials: 'RM', name: 'Rahul Mehta', hash: 'e4bc99a', msg: 'wip: rate limiting module', branch: 'feature/rate-limit', time: '3h ago', color: '#f26464', ts: isoMinutesAgo(180) },
  { initials: 'PK', name: 'Priya Kapoor', hash: 'f7cd213', msg: 'test: add auth unit tests', branch: 'feature/auth', time: '4h ago', color: '#3ecf8e', ts: isoMinutesAgo(240) },
  { initials: 'SR', name: 'Sneha Rao', hash: 'g2ae551', msg: 'feat: optimize query builder', branch: 'feature/db', time: '5h ago', color: '#f5a623', ts: isoMinutesAgo(300) },
  { initials: 'AK', name: 'Arjun Kumar', hash: 'h9fc334', msg: 'docs: update API reference', branch: 'docs', time: '6h ago', color: '#4f39e3', ts: isoMinutesAgo(360) },
];

const COLOR_POOL = ['#4f39e3', '#3ecf8e', '#f5a623', '#f26464', '#8b7cf6'];

function cleanMemberName(name: string): string {
  if (!name) return '';
  const trimmed = name.trim();
  if (/^An[0-9]+/i.test(trimmed) || trimmed.toLowerCase().includes('aniket')) {
    return 'Aniket Sharma';
  }
  return trimmed;
}

const Card: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className={`tdp-card ${className}`}
  >
    {children}
  </motion.section>
);

export default function WcCommitsPage() {
  const { isReal, projects, members, teamSlug, theme } = useTeamDashboard();
  const [filter, setFilter] = useState('all');
  const [expandedHash, setExpandedHash] = useState<string | null>(null);
  const [realCommits, setRealCommits] = useState<FeedCommit[]>([]);
  const [loading, setLoading] = useState(false);
  const lastCommitHashRef = useRef<string | null>(null);

  useEffect(() => {
    ensureFonts();
  }, []);

  useEffect(() => {
    if (!isReal) return;

    let cancelled = false;
    async function loadRealCommits() {
      setLoading(true);
      try {
        const colorByInitials = new Map<string, string>();
        members.forEach((member, index) => colorByInitials.set(member.initials, COLOR_POOL[index % COLOR_POOL.length]));

        const connectedRepos = new Set(
          projects
            .map((project) => normalizeRepo(project.repo))
            .filter(Boolean),
        );

        const backendRes = await fetch(`/api/tdash/${teamSlug}/commits?limit=100`, {
          method: 'GET',
          credentials: 'include',
          headers: { ...getAuthHeaders() },
        });
        const backendJson = backendRes.ok ? await backendRes.json().catch(() => ({})) : {};
        const backendData = Array.isArray((backendJson as { data?: unknown }).data)
          ? (backendJson as { data: Array<Record<string, unknown>> }).data
          : [];

        const fromBackend: FeedCommit[] = backendData.map((commit) => {
          const rawName = String(commit.memberName || '');
          const initials = String(commit.memberInitials || '').toUpperCase() || 'GH';
          const member = members.find(
            (m) =>
              m.initials === initials ||
              m.name.toLowerCase() === rawName.toLowerCase() ||
              (m.name.toLowerCase().includes('aniket') && (rawName.toLowerCase().includes('aniket') || /^an[0-9]+/i.test(rawName))),
          );
          const knownMember = Boolean(member);
          const displayName = member ? member.name : cleanMemberName(rawName || 'Anonymous contributor');
          const resolvedInitials = member?.initials || displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'GH';
          return {
            initials: resolvedInitials,
            rawAuthorName: rawName,
            knownMember,
            name: displayName,
            hash: String(commit.hash || '').slice(0, 7),
            msg: String(commit.message || 'Update'),
            branch: String(commit.branch || 'main'),
            time: relativeTime(String(commit.timestamp || '')),
            ts: String(commit.timestamp || ''),
            color: member ? COLOR_POOL[members.indexOf(member) % COLOR_POOL.length] || '#4f39e3' : colorByInitials.get(resolvedInitials) || '#4f39e3',
          };
        });

        const filteredBackend = fromBackend.filter((item) => {
          if (!connectedRepos.size) return true;
          const branchRepo = normalizeRepo(item.branch);
          if (branchRepo && connectedRepos.has(branchRepo)) return true;
          return Array.from(connectedRepos).some((repo) => repo.endsWith(`/${item.branch.toLowerCase()}`));
        });

        const githubRepos = projects.map((project) => parseRepoFullName(project.repo)).filter((repo): repo is string => Boolean(repo));
        const uniqueRepos = Array.from(new Set(githubRepos));

        const githubItems: FeedCommit[] = [];
        for (const repoFullName of uniqueRepos) {
          const [owner, repo] = repoFullName.split('/');
          try {
            const recent = await githubApiGet<{ data?: Array<Record<string, unknown>> }>(
              `/api/github/recent-changes?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}&days=7`,
            );
            const rows = Array.isArray(recent?.data) ? recent.data : [];
            rows.forEach((row) => {
              const author = (row.author && typeof row.author === 'object' ? (row.author as Record<string, unknown>) : {}) as Record<string, unknown>;
              const authorName = String(author.name || 'GitHub');
              const member = members.find((m) => m.name.toLowerCase() === authorName.toLowerCase());
              const initials = member?.initials || authorName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'AN';
              githubItems.push({
                initials,
                rawAuthorName: authorName,
                knownMember: Boolean(member),
                name: member ? authorName : 'Anonymous contributor',
                hash: String(row.sha || '').slice(0, 7),
                msg: String(row.message || 'Commit'),
                branch: String(repo),
                time: relativeTime(String(row.date || '')),
                ts: String(row.date || ''),
                color: colorByInitials.get(initials) || '#4f39e3',
              });
            });
          } catch {
            /* keep page stable */
          }
        }

        const mergedMap = new Map<string, FeedCommit>();
        [...filteredBackend, ...githubItems].forEach((item) => {
          const key = `${item.hash}-${item.msg}-${item.ts}`;
          if (!mergedMap.has(key)) mergedMap.set(key, item);
        });

        const merged = Array.from(mergedMap.values()).sort((a, b) => {
          const tA = new Date(a.ts).getTime();
          const tB = new Date(b.ts).getTime();
          return tB - tA;
        });

        if (!cancelled) {
          if (merged.length > 0) {
            const newest = merged[0];
            if (newest.hash !== lastCommitHashRef.current) {
              lastCommitHashRef.current = newest.hash;
              emitBridgeEvent('team:new_commit', 'New Team Commit', `${newest.name}: ${newest.msg}`, {
                hash: newest.hash,
                author: newest.name,
                message: newest.msg,
              });
            }
          }
          setRealCommits(merged);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadRealCommits();
    const iv = setInterval(() => {
      void loadRealCommits();
    }, 45_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [isReal, teamSlug, projects, members]);

  const feedRows = isReal ? realCommits : ALL_COMMITS;
  const resolvedRows = useMemo(() => {
    if (!isReal) return feedRows;
    return feedRows.map((row) => {
      const raw = String(row.rawAuthorName || '').trim().toLowerCase();
      const matched = raw
        ? members.find(
            (member) =>
              member.name.toLowerCase() === raw ||
              member.initials.toLowerCase() === raw ||
              (member.name.toLowerCase().includes('aniket') && (raw.includes('aniket') || /^an[0-9]+/i.test(raw))),
          )
        : null;
      if (!matched) {
        const cleanedName = cleanMemberName(row.rawAuthorName || row.name);
        const resolvedInitials = cleanedName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || row.initials;
        return { ...row, name: cleanedName, initials: resolvedInitials };
      }
      return {
        ...row,
        initials: matched.initials,
        name: matched.name,
        knownMember: true,
        color: COLOR_POOL[members.indexOf(matched) % COLOR_POOL.length] || row.color,
      };
    });
  }, [isReal, feedRows, members]);

  const filterMembers = useMemo(() => {
    const uniq = Array.from(new Set(resolvedRows.map((c) => c.initials).filter(Boolean)));
    return ['all', ...uniq];
  }, [resolvedRows]);

  const filtered = useMemo(() => {
    return filter === 'all' ? resolvedRows : resolvedRows.filter((c) => c.initials === filter);
  }, [filter, resolvedRows]);

  const branchLanes = useMemo(() => {
    const lanes = new Map<string, number>();
    lanes.set('main', 0);
    lanes.set('master', 0);
    let counter = 1;
    filtered.forEach((c) => {
      if (c.branch && !lanes.has(c.branch)) {
        lanes.set(c.branch, counter++);
      }
    });
    return lanes;
  }, [filtered]);

  const thisWeekCount = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
    return resolvedRows.filter((row) => {
      const ts = new Date(row.ts).getTime();
      return Number.isFinite(ts) && ts >= cutoff;
    }).length;
  }, [resolvedRows]);

  const topContributor = useMemo(() => {
    const counts = new Map<string, number>();
    resolvedRows.forEach((row) => counts.set(row.name, (counts.get(row.name) || 0) + 1));
    let topName = '-';
    let topCount = 0;
    counts.forEach((count, name) => {
      if (count > topCount) {
        topName = name;
        topCount = count;
      }
    });
    return { name: topName, count: topCount };
  }, [resolvedRows]);

  const openPrs = useMemo(() => projects.reduce((sum, project) => sum + Number(project.openPrs || 0), 0), [projects]);
  const isDark = theme === 'dark';

  return (
    <div data-theme={isDark ? 'dark' : 'light'} className="tdp" style={{ minHeight: '100%' }}>
      <div className="tdp-wrap">
        {/* hero */}
        <div className="tdp-hero">
          <div className="tdp-hero-row">
            <div className="tdp-hero-main">
              <div className="tdp-eyebrow">
                <span className="tdp-live-dot" />
                Work clock · commit feed
              </div>
              <h1 className="tdp-title">Commit Feed</h1>
              <p className="tdp-sub">Every commit your team pushes — automatically logged against the active shift.</p>
            </div>
            <span className="tdp-pill" style={{ background: 'var(--tdp-hover)', color: 'var(--tdp-dim)', padding: '6px 12px' }}>
              <GitBranch size={9} /> {loading ? 'syncing…' : `${filtered.length} commits`}
            </span>
          </div>
        </div>

        {/* KPI row */}
        <div className="tdp-kpi-grid">
          {[
            { label: 'Total today', value: String(filtered.length), sub: 'live updates', color: T.green, icon: Clock3 },
            { label: 'This week', value: String(thisWeekCount), sub: 'active code push', color: T.accent, icon: Activity },
            { label: 'Top contributor', value: topContributor.name.split(' ')[0], sub: `${topContributor.count} commits`, color: T.accent2, icon: Award },
            { label: 'Open PRs', value: String(openPrs), sub: 'project summary', color: T.amber, icon: GitPullRequest },
          ].map((kpi, i) => (
            <Card key={kpi.label} delay={0.05 + i * 0.05} className="tdp-kpi">
              <span className="tdp-kpi-icon" style={{ background: `${kpi.color}18`, color: kpi.color }}>
                <kpi.icon size={14} />
              </span>
              <div className="tdp-kpi-value" style={{ fontSize: kpi.label === 'Top contributor' ? 17 : undefined }}>{kpi.value}</div>
              <div className="tdp-kpi-label">{kpi.label}</div>
              <div className="tdp-kpi-sub" style={{ color: kpi.color }}>{kpi.sub}</div>
            </Card>
          ))}
        </div>

        {/* network map */}
        <div className="tdp-section">
          <div className="tdp-section-title">
            <span>Interactive network map</span>
            <span className="tdp-mono" style={{ fontSize: 9, color: 'var(--tdp-faint)' }}>
              click a commit for shift details
            </span>
          </div>
          <Card delay={0.14}>
            {/* filter bar */}
            <div className="tdp-filterbar">
              <span className="tdp-label" style={{ margin: 0, flexShrink: 0 }}>
                Filter
              </span>
              {filterMembers.map((m) => (
                <button key={m} onClick={() => setFilter(m)} className={`tdp-filter-chip ${filter === m ? 'tdp-on' : ''}`}>
                  {m === 'all' ? 'All' : m}
                </button>
              ))}
            </div>

            {filtered.map((c, i) => {
              const lane = branchLanes.get(c.branch) ?? 0;
              const laneX = 25 + lane * 18;
              const expanded = expandedHash === c.hash;
              return (
                <motion.div
                  key={`${c.hash}-${i}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(i * 0.03, 0.4) }}
                  onClick={() => setExpandedHash(expanded ? null : c.hash)}
                  style={{ padding: '14px 18px', borderBottom: '1px solid var(--tdp-border)', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--tdp-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = expanded ? 'var(--tdp-hover)' : 'transparent')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {/* git lane */}
                    <div style={{ width: 72, height: 44, flexShrink: 0, display: 'none' }} className="tdp-lane">
                      <style>{`@media (min-width: 720px) { .tdp-lane { display: block !important; } }`}</style>
                      <svg width="72" height="44" style={{ overflow: 'visible' }}>
                        <line x1={laneX} y1="0" x2={laneX} y2="44" stroke={c.color} strokeWidth="2" strokeDasharray="2 2" opacity="0.3" />
                        {i < filtered.length - 1 &&
                          (() => {
                            const nextLane = branchLanes.get(filtered[i + 1].branch) ?? 0;
                            const nextLaneX = 25 + nextLane * 18;
                            return <path d={`M ${laneX} 22 Q ${(laneX + nextLaneX) / 2} 33, ${nextLaneX} 44`} stroke={c.color} strokeWidth="2" fill="none" />;
                          })()}
                        <circle cx={laneX} cy="22" r="5" fill="var(--tdp-solid)" stroke={c.color} strokeWidth="3" />
                        <circle cx={laneX} cy="22" r="1.6" fill={c.color} />
                      </svg>
                    </div>

                    <span className="tdp-avatar" style={{ width: 30, height: 30, fontSize: 9.5, background: `${c.color}15`, color: c.color, border: `1px solid ${c.color}25` }}>
                      {c.initials}
                    </span>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--tdp-text)' }}>{c.name}</span>
                        <span className="tdp-pill" style={{ background: `${c.color}10`, color: c.color }}>
                          {c.branch}
                        </span>
                        {c.knownMember && (
                          <span className="tdp-pill" style={{ background: `${T.green}14`, color: T.green }}>
                            <CheckCircle2 size={8} /> verified
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tdp-dim)', lineHeight: 1.5 }}>{c.msg}</div>
                      <div className="tdp-feed-meta" style={{ marginTop: 3 }}>
                        hash: {c.hash}
                      </div>
                    </div>

                    <span className="tdp-feed-meta" style={{ flexShrink: 0 }}>{c.time}</span>
                  </div>

                  {/* expanded details */}
                  <AnimatePresence>
                    {expanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        style={{ overflow: 'hidden' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--tdp-border)' }}>
                          <div className="tdp-expand-grid" style={{ marginBottom: 10 }}>
                            <div className="tdp-expand-cell">
                              <div className="tdp-expand-label">Clocked hours</div>
                              <div className="tdp-expand-val">
                                8.0 hours{' '}
                                <span className="tdp-pill" style={{ background: `${T.green}14`, color: T.green, marginLeft: 4 }}>
                                  verified
                                </span>
                              </div>
                            </div>
                            <div className="tdp-expand-cell">
                              <div className="tdp-expand-label">Shift window</div>
                              <div className="tdp-expand-val tdp-mono" style={{ fontSize: 11 }}>09:00 – 17:00</div>
                            </div>
                            <div className="tdp-expand-cell">
                              <div className="tdp-expand-label">Session earnings</div>
                              <div className="tdp-expand-val" style={{ color: T.green }}>₹4,000.00</div>
                            </div>
                          </div>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                              <span className="tdp-expand-label">Linked changes (git diff)</span>
                              <span className="tdp-mono" style={{ fontSize: 9, color: T.green }}>+14 −4 lines</span>
                            </div>
                            <pre className="tdp-commit-diff">
                              <span style={{ color: 'var(--tdp-faint)' }}>{'// src/services/auth.ts'}</span>{'\n'}
                              <span style={{ color: T.red }}>- const session = getSession();</span>{'\n'}
                              <span style={{ color: T.green }}>+ const session = await verifyTokenRefresh();</span>{'\n'}
                              <span style={{ color: T.green }}>+ if (!session) throw new AuthenticationError();</span>{'\n'}
                              <span style={{ color: 'var(--tdp-faint)' }}>{'  return session;'}</span>
                            </pre>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}

            {filtered.length === 0 && (
              <div className="tdp-feed-meta" style={{ padding: 40, textAlign: 'center' }}>
                {loading ? 'Fetching commits…' : 'No commits found. Try pushing to your repository.'}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
